import { createClient } from '@supabase/supabase-js';
import { logger, LogCategory } from './logger';

// Supabase URL and anon key from environment variables
const supabaseUrl = process.env.VUE_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.VUE_APP_SUPABASE_ANON_KEY || '';
const supabaseServiceKey = process.env.VUE_APP_SUPABASE_SERVICE_KEY || '';

// Log environment variable status
logger.info('Environment variables loaded', LogCategory.GENERAL, {
  supabaseUrl: supabaseUrl ? 'Available' : 'Missing',
  supabaseAnonKey: supabaseAnonKey ? 'Available' : 'Missing',
  supabaseServiceKey: supabaseServiceKey ? 'Available' : 'Missing'
});

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  const errorMsg = 'Missing required Supabase environment variables: VUE_APP_SUPABASE_URL and VUE_APP_SUPABASE_ANON_KEY must be set in .env file.';
  logger.error(errorMsg, LogCategory.SECURITY);
  throw new Error(errorMsg);
}

// Storage key for auth session
const storageKey = process.env.VUE_APP_SUPABASE_STORAGE_KEY || 'fraport-supabase-auth';

// Create a regular client for user operations
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

// Create admin client with service role key (if available)
export const supabaseAdmin = supabaseServiceKey
  ? createClient(supabaseUrl, supabaseServiceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    })
  : null;

// Function to get appropriate client based on role
export function getSupabaseClient(role: 'anon' | 'service' = 'anon') {
  if (role === 'service' && supabaseAdmin) {
    return supabaseAdmin;
  }
  return supabase;
}

// Log connection attempts
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_IN') {
    logger.info('User signed in', LogCategory.AUTH, { userId: session?.user?.id });
  } else if (event === 'SIGNED_OUT') {
    logger.info('User signed out', LogCategory.AUTH);
  } else if (event === 'TOKEN_REFRESHED') {
    logger.debug('Auth token refreshed', LogCategory.AUTH);
  }
});

// Test and log connection status
setTimeout(async () => {
  try {
    const { error } = await supabase.from('profiles').select('count').limit(1);
    if (error) {
      logger.error('Failed to establish Supabase connection', LogCategory.DATABASE, { error });
    } else {
      logger.info('Supabase connection established', LogCategory.DATABASE, { timestamp: new Date().toISOString() });
    }
  } catch (err) {
    logger.error('Failed to establish Supabase connection', LogCategory.DATABASE, { error: err });
  }
}, 1000); // Delay the test to ensure the client is fully initialized

// Export types for better TypeScript integration
export type { SupabaseClient } from '@supabase/supabase-js';
